import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import { ArrowLeft, Lightbulb, Send } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import { submitRecommendation } from '../../lib/api/recommendations';
import toast from 'react-hot-toast';

export function Recommendations() {
  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  const [recommendation, setRecommendation] = useState('');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);

  if (!selectedLeague || !currentUserId) {
    navigate('/dashboard');
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!recommendation.trim()) {
      toast.error(translate('recommendation.error', language));
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await submitRecommendation(
        category,
        recommendation.trim(),
        parseInt(currentUserId, 10)
      );

      if (error) {
        throw new Error(error);
      }

      toast.success(translate('recommendation.sent', language));
      setRecommendation('');
      setCategory('general');
    } catch (error: any) {
      console.error('Error submitting recommendation:', error);
      toast.error(translate('recommendation.error', language));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => goBackOrHome(navigate)}
        >
          <ArrowLeft size={20} className="me-2" />
          {translate('back', language)}
        </button>

        <div className="text-center mb-4">
          <Lightbulb className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-4">
            {translate('system.recommendations', language)}
          </h2>
          <p className="text-muted">{selectedLeague.name}</p>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-8">
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="form-label">
                  {translate('recommendation.category', language)}
                </label>
                <select
                  className="form-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                >
                  <option value="general">{translate('category.general', language)}</option>
                  <option value="trading">{translate('category.trading', language)}</option>
                  <option value="roster">{translate('category.roster', language)}</option>
                  <option value="scoring">{translate('category.scoring', language)}</option>
                  <option value="interface">{translate('category.interface', language)}</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="form-label">
                  {translate('recommendation.description', language)}
                </label>
                <textarea
                  className="form-control"
                  rows={6}
                  value={recommendation}
                  onChange={(e) => setRecommendation(e.target.value)}
                  placeholder={translate('recommendation.placeholder', language)}
                  required
                  minLength={10}
                  maxLength={2000}
                ></textarea>
              </div>

              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
                  disabled={submitting || !recommendation.trim()}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      {translate('sending', language)}
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      {translate('send.recommendation', language)}
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="mt-5">
              <h5 className="mb-3">{translate('recommendation.guidelines', language)}</h5>
              <ul className="list-group">
                <li className="list-group-item">
                  <div className="d-flex align-items-center">
                    <Lightbulb size={18} className="text-primary me-2" />
                    {translate('guideline.clear', language)}
                  </div>
                </li>
                <li className="list-group-item">
                  <div className="d-flex align-items-center">
                    <Lightbulb size={18} className="text-primary me-2" />
                    {translate('guideline.specific', language)}
                  </div>
                </li>
                <li className="list-group-item">
                  <div className="d-flex align-items-center">
                    <Lightbulb size={18} className="text-primary me-2" />
                    {translate('guideline.constructive', language)}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}